import { useEffect, useRef, useState } from "react";
import { V86Starter } from "v86";
import { v86WASM, seabios, vgabios } from "v86/build/binaries";

interface SystemStats {
  uptime: number;
  totalMemory: number;
  vgaMemory: number;
}

export default function App() {
  const divRef = useRef<HTMLDivElement>(null);
  const emulatorRef = useRef<any>(null);
  const [stats, setStats] = useState<SystemStats>({
    uptime: 0,
    totalMemory: 32 * 1024 * 1024, // 32MB configured
    vgaMemory: 4 * 1024 * 1024, // 4MB VGA memory
  });
  const [currentStatusIndex, setCurrentStatusIndex] = useState(0);

  useEffect(() => {
    let startTime = Date.now();
    let keydownCleanup: (() => void) | null = null;

    async function main() {
      const emulator = new V86Starter({
        wasm_fn: async (param: any) =>
          (await WebAssembly.instantiate(await v86WASM, param)).instance
            .exports,
        memory_size: 32 * 1024 * 1024,
        vga_memory_size: 4 * 1024 * 1024,
        screen_container: divRef.current,
        bios: { buffer: await seabios },
        vga_bios: { buffer: await vgabios },
        hda: { url: "os.img" },
        autostart: true
        // Mouse is enabled by default (disable_mouse: false)
      });

      emulatorRef.current = emulator;

      // Aggressively enable mouse support
      let mouseEnabled = false;
      const enableMouse = () => {
        if (!mouseEnabled && emulator.mouse_set_enabled) {
          console.log("Force enabling mouse");
          emulator.mouse_set_enabled(true);
          mouseEnabled = true;
        }
      };

      // Enable mouse on multiple events
      emulator.add_listener("emulator-ready", enableMouse);
      emulator.add_listener("emulator-started", enableMouse);
      
      // Also enable mouse after a short delay to catch any timing issues
      setTimeout(enableMouse, 1000);
      setTimeout(enableMouse, 3000);

      // Listen for mouse enable events from the OS
      emulator.add_listener("mouse-enable", (enabled: boolean) => {
        console.log("OS mouse enable status:", enabled);
        if (enabled) {
          mouseEnabled = true;
        }
      });

      // Add mouse lock functionality
      if (divRef.current) {
        // Click to lock mouse
        divRef.current.addEventListener('click', () => {
          console.log("Attempting to lock mouse");
          enableMouse(); // Ensure mouse is enabled before locking
          if (emulator && emulator.lock_mouse) {
            emulator.lock_mouse();
          }
        });
      }

      // ESC to unlock mouse
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && document.pointerLockElement) {
          document.exitPointerLock();
        }
      };
      
      document.addEventListener('keydown', handleKeyDown);
      keydownCleanup = () => document.removeEventListener('keydown', handleKeyDown);
    }

    function updateStats() {
      const now = Date.now();
      const uptime = Math.floor((now - startTime) / 1000);
      
      setStats(prev => ({
        ...prev,
        uptime
      }));
      
      requestAnimationFrame(updateStats);
    }

    // Cycle through status displays every 3 seconds
    const statusInterval = setInterval(() => {
      setCurrentStatusIndex(prev => (prev + 1) % 4);
    }, 3000);

    main();
    updateStats();

    return () => {
      if (emulatorRef.current) {
        // Cleanup if needed
      }
      clearInterval(statusInterval);
      if (keydownCleanup) {
        keydownCleanup();
      }
    };
  }, []);

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatUptime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getCurrentStatus = () => {
    const statusData = [
      { label: 'Uptime', value: formatUptime(stats.uptime), icon: '⏱️' },
      { label: 'Architecture', value: 'x86-32', icon: '🖥️' },
      { label: 'RAM', value: formatBytes(stats.totalMemory), icon: '💾' },
      { label: 'VGA', value: formatBytes(stats.vgaMemory), icon: '🎮' }
    ];
    return statusData[currentStatusIndex];
  };

  return (
    <div 
      className="App"
      style={{
        minHeight: '100vh',
        width: '100vw',
        backgroundImage: 'url(/background.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px',
        boxSizing: 'border-box',
        gap: '16px',
        margin: 0,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }}
    >
      {/* Global CSS Reset */}
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        html, body {
          margin: 0;
          padding: 0;
          width: 100%;
          height: 100%;
          overflow-x: hidden;
        }
        
        #root {
          width: 100%;
          height: 100%;
        }
      `}</style>
      {/* Top row with logo, navigation, and status */}
      <div style={{
        position: 'absolute',
        top: '20px',
        left: '20px',
        right: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        zIndex: 20
      }}>
        {/* Left side - Alano Logo */}
        <a 
          href="https://crm.alano.ai/" 
          target="_blank" 
          rel="noopener noreferrer"
          style={{
            textDecoration: 'none'
          }}
        >
          <img 
            src="/alano.svg" 
            alt="Alano.ai - Click to visit our CRM" 
            style={{
              height: '48px',
              width: 'auto',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
              cursor: 'pointer',
              transition: 'transform 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          />
        </a>

        {/* Right side - Status indicators */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '8px'
        }}>
          {/* Status buttons */}
          <div style={{
            display: 'flex',
            gap: '8px'
          }}>
            {/* Uptime */}
            <div 
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #60a5fa, #3b82f6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                opacity: currentStatusIndex === 0 ? 1 : 0.6,
                transition: 'opacity 0.3s ease'
              }}
            >
              ⏱️
            </div>

            {/* Architecture */}
            <div 
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                opacity: currentStatusIndex === 1 ? 1 : 0.6,
                transition: 'opacity 0.3s ease'
              }}
            >
              🖥️
            </div>

            {/* Memory */}
            <div 
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                opacity: currentStatusIndex === 2 ? 1 : 0.6,
                transition: 'opacity 0.3s ease'
              }}
            >
              💾
            </div>

            {/* VGA Memory */}
            <div 
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #ec4899, #be185d)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                opacity: currentStatusIndex === 3 ? 1 : 0.6,
                transition: 'opacity 0.3s ease'
              }}
            >
              🎮
            </div>
          </div>

          {/* Current Status Display */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '8px',
            padding: '6px 10px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
            fontSize: '11px',
            color: 'white',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            minWidth: '120px'
          }}>
            <span style={{ fontSize: '12px' }}>{getCurrentStatus().icon}</span>
            <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{getCurrentStatus().label}:</span>
            <span style={{ fontWeight: '600' }}>{getCurrentStatus().value}</span>
          </div>
        </div>
      </div>

      {/* Perfectly centered navigation button */}
      <div style={{
        position: 'absolute',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 21,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px'
      }}>
        <div style={{
          background: 'rgba(0, 0, 0, 0.6)',
          color: 'white',
          padding: '6px 12px',
          borderRadius: '16px',
          fontSize: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          ⌨️ Use keyboard to navigate
        </div>
      </div>

      {/* Main Emulator Container */}
      <div
        style={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          border: '20px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '16px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
          overflow: 'hidden',
          position: 'relative',
          // Fixed aspect ratio container (4:3)
          width: 'min(90vw, calc(90vh * 4/3))',
          height: 'min(90vh, calc(90vw * 3/4))',
          aspectRatio: '4/3'
        }}
      >
        <div 
          ref={divRef}
          style={{
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 10px 25px -3px rgba(0, 0, 0, 0.3)',
            background: '#000',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div
            style={{ 
              whiteSpace: "pre", 
              font: "14px monospace", 
              lineHeight: 14,
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
          ></div>
          <canvas 
            style={{ 
              display: "block",
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain'
            }}
          ></canvas>
        </div>
      </div>
    </div>
  );
}
